import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Alert,
  Chip,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save,
  Refresh,
  VolumeUp,
  Person,
  Psychology,
  SupervisorAccount,
  Settings,
  Group
} from '@mui/icons-material';
import api from '../../../services/api';
import { getElevenLabsVoices } from '../../../services/api';
import TextToSpeech from '../../TextToSpeech';

const roleConfigs = [
  {
    key: 'ai_therapist',
    label: 'AI Therapist',
    icon: <Psychology />,
    color: '#6366f1',
    description: 'Voice used for AI therapist responses'
  },
  {
    key: 'human_therapist',
    label: 'Human Therapist',
    icon: <Person />,
    color: '#10b981',
    description: 'Voice for human therapist messages in recordings'
  },
  {
    key: 'client',
    label: 'Client',
    icon: <Group />,
    color: '#f59e0b',
    description: 'Default voice for client text-to-speech'
  },
  {
    key: 'supervisor',
    label: 'Supervisor',
    icon: <SupervisorAccount />,
    color: '#8b5cf6',
    description: 'Voice for supervisor consultations'
  },
  {
    key: 'system',
    label: 'System',
    icon: <Settings />,
    color: '#64748b',
    description: 'Voice for system messages and notifications'
  }
];

function RoleVoiceConfiguration({ token, configuration, onConfigChange }) {
  const [selectedProvider, setSelectedProvider] = useState('elevenlabs');
  const [providers, setProviders] = useState([]);
  const [voices, setVoices] = useState({});
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [roleVoices, setRoleVoices] = useState({});
  const [sampleText, setSampleText] = useState('Hello, this is a sample of the selected voice for this role.');
  const [saveStatus, setSaveStatus] = useState(null);
  const [testingVoice, setTestingVoice] = useState({});

  useEffect(() => {
    loadProviders();
    if (configuration?.platformRoleVoices) {
      // Ensure the structure is correct for provider-specific voices
      const formattedVoices = {};
      Object.keys(configuration.platformRoleVoices).forEach(role => {
        const roleConfig = configuration.platformRoleVoices[role];
        if (typeof roleConfig === 'object' && roleConfig !== null) {
          formattedVoices[role] = roleConfig;
        } else {
          // Handle legacy format where it might just be a string
          formattedVoices[role] = { primary: roleConfig };
        }
      });
      setRoleVoices(formattedVoices);
    }
  }, [configuration]);

  useEffect(() => {
    if (selectedProvider) {
      loadVoicesForProvider(selectedProvider);
    }
  }, [selectedProvider]);

  const loadProviders = async () => {
    try {
      const response = await api.get('/tts/providers', token);
      console.log('Providers response:', response);
      
      // Access response.data for axios response
      const providerList = response.data?.providers || [];
      
      if (providerList.length > 0) {
        setProviders(providerList);
        // Select first available provider
        const firstAvailable = providerList.find(p => p.available);
        if (firstAvailable) {
          setSelectedProvider(firstAvailable.name);
        }
      } else {
        console.warn('No providers returned from API');
        // Set default providers as fallback
        const defaultProviders = [
          {
            name: 'elevenlabs',
            displayName: 'ElevenLabs',
            available: true,
            costPer1000Chars: 0.18
          },
          {
            name: 'google',
            displayName: 'Google Cloud TTS',
            available: true,
            costPer1000Chars: 0.016
          }
        ];
        setProviders(defaultProviders);
        setSelectedProvider('elevenlabs');
      }
    } catch (error) {
      console.error('Failed to load providers:', error);
      // Set default providers on error
      const defaultProviders = [
        {
          name: 'elevenlabs',
          displayName: 'ElevenLabs',
          available: true,
          costPer1000Chars: 0.18
        },
        {
          name: 'google',
          displayName: 'Google Cloud TTS',
          available: true,
          costPer1000Chars: 0.016
        }
      ];
      setProviders(defaultProviders);
      setSelectedProvider('elevenlabs');
    }
  };

  const loadVoicesForProvider = async (provider) => {
    try {
      setLoadingVoices(true);
      
      if (provider === 'elevenlabs') {
        // Use the same method as ElevenLabsConfiguration for consistency
        const elevenLabsVoices = await getElevenLabsVoices();
        console.log(`ElevenLabs voices loaded:`, elevenLabsVoices);
        
        setVoices(prev => ({
          ...prev,
          [provider]: elevenLabsVoices || []
        }));
      } else {
        // For other providers, use the backend API
        const response = await api.get(`/tts/voices?provider=${provider}`, token);
        console.log(`Voices response for ${provider}:`, response);
        
        // Access response.data for axios response
        const voiceList = response.data?.voices || [];
        
        setVoices(prev => ({
          ...prev,
          [provider]: voiceList
        }));
      }
    } catch (error) {
      console.error(`Failed to load voices for ${provider}:`, error);
      // For Google TTS, set some default voices as example
      if (provider === 'google') {
        setVoices(prev => ({
          ...prev,
          [provider]: [
            { id: 'en-GB-Standard-A', name: 'British English - Female A', labels: { gender: 'Female', accent: 'British' } },
            { id: 'en-GB-Standard-B', name: 'British English - Male B', labels: { gender: 'Male', accent: 'British' } },
            { id: 'en-US-Standard-C', name: 'US English - Female C', labels: { gender: 'Female', accent: 'American' } },
            { id: 'en-US-Standard-D', name: 'US English - Male D', labels: { gender: 'Male', accent: 'American' } }
          ]
        }));
      }
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleVoiceChange = (role, voiceId) => {
    setRoleVoices(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [selectedProvider]: voiceId
        // Don't update primary - let each provider have its own voice
      }
    }));
  };

  const handleTestVoice = async (role, voiceId) => {
    if (!voiceId) return;
    
    try {
      setTestingVoice(prev => ({ ...prev, [role]: true }));
      
      console.log(`Testing voice for ${role} with provider ${selectedProvider} and voiceId ${voiceId}`);
      
      const response = await api.post('/tts/test', {
        text: sampleText,
        voiceId: voiceId,
        provider: selectedProvider,
        role: role
      }, token);

      if (response.data?.success && response.data?.audio) {
        // Play the audio
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        await audio.play();
      } else {
        console.error('No audio returned from test endpoint');
        setSaveStatus({ type: 'error', message: 'No audio returned from test' });
      }
    } catch (error) {
      console.error('Failed to test voice:', error);
      setSaveStatus({ type: 'error', message: `Failed to test voice: ${error.message}` });
    } finally {
      setTestingVoice(prev => ({ ...prev, [role]: false }));
    }
  };

  const handleSave = async () => {
    try {
      // Update configuration with role voices for all providers
      const updatedConfig = {
        ...configuration,
        platformRoleVoices: roleVoices
      };

      const response = await api.put('/tts/configuration', updatedConfig, token);
      
      if (response.data?.success) {
        setSaveStatus({ type: 'success', message: 'Voice configuration saved successfully' });
        if (onConfigChange) {
          onConfigChange(updatedConfig);
        }
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save configuration' });
    }
  };

  const currentProvider = providers.find(p => p.name === selectedProvider);
  const currentVoices = voices[selectedProvider] || [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Role-Based Voice Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure default voices for each role across all TTS providers.
      </Typography>

      {/* Provider Selection */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1">
              Select Provider
            </Typography>
            <Button
              startIcon={<Refresh />}
              onClick={() => loadVoicesForProvider(selectedProvider)}
              disabled={loadingVoices}
              size="small"
            >
              Refresh Voices
            </Button>
          </Box>

          <Tabs
            value={selectedProvider}
            onChange={(e, value) => setSelectedProvider(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {providers.map(provider => (
              <Tab
                key={provider.name}
                value={provider.name}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {provider.displayName}
                    {provider.available ? (
                      <Chip label="Available" size="small" color="success" />
                    ) : (
                      <Chip label="Not Configured" size="small" />
                    )}
                  </Box>
                }
                disabled={!provider.available}
              />
            ))}
          </Tabs>

          {currentProvider && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Cost: ${currentProvider.costPer1000Chars}/1000 characters
              </Typography>
              {loadingVoices && (
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading voices...</Typography>
                </Box>
              )}
              {!loadingVoices && currentVoices.length > 0 && (
                <Typography variant="body2" color="primary">
                  {currentVoices.length} voices available
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Role Voice Configuration */}
      <Grid container spacing={2}>
        {roleConfigs.map(role => {
          // Get the voice ID for the current provider only
          const voiceId = roleVoices[role.key]?.[selectedProvider] || '';
          
          return (
            <Grid item xs={12} md={6} key={role.key}>
              <Card 
                variant="outlined"
                sx={{ 
                  borderColor: voiceId ? role.color : 'divider',
                  borderWidth: voiceId ? 2 : 1
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box sx={{ color: role.color }}>
                      {role.icon}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="subtitle1">{role.label}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </Box>

                  <FormControl fullWidth size="small" margin="normal">
                    <InputLabel>Select Voice</InputLabel>
                    <Select
                      value={voiceId}
                      onChange={(e) => handleVoiceChange(role.key, e.target.value)}
                      label="Select Voice"
                      disabled={loadingVoices || currentVoices.length === 0}
                    >
                      <MenuItem value="">
                        <em>None (Use System Default)</em>
                      </MenuItem>
                      {currentVoices.map(voice => {
                        // Handle both ElevenLabs (voice_id) and other providers (id)
                        const voiceId = voice.voice_id || voice.id;
                        const voiceName = voice.name;
                        
                        return (
                          <MenuItem key={voiceId} value={voiceId}>
                            <Box>
                              <Typography variant="body2">
                                {voiceName}
                              </Typography>
                              {voice.labels && (
                                <Typography variant="caption" color="textSecondary">
                                  {[
                                    voice.labels.gender,
                                    voice.labels.accent,
                                    voice.labels.age,
                                    voice.labels.useCase
                                  ].filter(Boolean).join(' • ')}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {/* Direct Voice ID Input */}
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    label="Or Enter Voice ID Directly"
                    value={voiceId}
                    onChange={(e) => handleVoiceChange(role.key, e.target.value)}
                    placeholder={`e.g., ${selectedProvider === 'google' ? 'en-GB-Standard-A' : 'voice_id_here'}`}
                    helperText="You can also paste a voice ID directly"
                  />

                  {/* Test Voice Button */}
                  {voiceId && (
                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={testingVoice[role.key] ? <CircularProgress size={16} /> : <VolumeUp />}
                        onClick={() => handleTestVoice(role.key, voiceId)}
                        disabled={testingVoice[role.key]}
                      >
                        Test Voice
                      </Button>
                      <Typography variant="caption" color="textSecondary">
                        Provider: {selectedProvider}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Sample Text Configuration */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Test Sample Text
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={sampleText}
            onChange={(e) => setSampleText(e.target.value)}
            placeholder="Enter text to test with selected voices"
            helperText="This text will be used when testing voices"
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={handleSave}
          size="large"
        >
          Save Voice Configuration
        </Button>
      </Box>

      {/* Status Messages */}
      {saveStatus && (
        <Alert 
          severity={saveStatus.type} 
          sx={{ mt: 2 }}
          onClose={() => setSaveStatus(null)}
        >
          {saveStatus.message}
        </Alert>
      )}

      {/* Information Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Voice Selection Hierarchy
        </Typography>
        <Typography variant="body2">
          The system will use voices in this priority order:
        </Typography>
        <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Client-specific voice overrides (set by therapists)</li>
          <li>Therapist default voices (per therapist settings)</li>
          <li>Interface-specific voices (per interface configuration)</li>
          <li>Provider-specific role voices (configured here)</li>
          <li>System fallback voices</li>
        </ol>
      </Alert>
    </Box>
  );
}

export default RoleVoiceConfiguration;